import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import axios from "axios";

const STORES_API_URL = "https://api.staging.lashbrookdesigns.com/api/admin/v1/stores";

const DEFAULT_OPTION = { id: null, store_name: "Default", name: "Default", isDefault: true };
const PAGE_SIZE = 10;
const PAGE_NUMBER = 1;
const SORT_FIELD = "id";

const getStoreLabel = (opt) => {
  if (!opt || opt.isDefault) return "Default";
  return opt.store_name || opt.name || `Store ${opt.id}`;
};

const redirectToLoginOnAuthError = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    window.location.replace('/#/login');
    return true;
  }
  return false;
};

const CustomStoreSelect = ({ storeId, onStoreIdChange, isDisabled = false }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [selectKey, setSelectKey] = useState(0);

  const fetchStores = useCallback((searchTerm = "") => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);

    return axios
      .get(STORES_API_URL, {
        signal: abortControllerRef.current.signal,
        params: {
          "filter[store_name]": searchTerm,
          "page[number]": PAGE_NUMBER,
          "page[size]": PAGE_SIZE,
          sort: SORT_FIELD,
        },
      })
      .then((res) => {
        const payload = res && res.data ? res.data : null;
        const stores = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : payload?.data
              ? payload.data
              : [];

        const normalizedStores = Array.isArray(stores) ? stores : [];
        setList(normalizedStores);
        return normalizedStores;
      })
      .catch((err) => {
        if (redirectToLoginOnAuthError(err)) {
          return [];
        }
        if (err && err.name !== "CanceledError") {
          setList([]);
        }
        return [];
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStores("");
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchStores]);

  const selectedValue = useMemo(() => {
    if (storeId === null || storeId === undefined || storeId === "") return DEFAULT_OPTION;
    const match =
      list.find((item) => String(item?.id) === String(storeId)) ||
      list.find((item) => String(item?.store_id) === String(storeId));
    return match || { id: storeId, store_name: "Selected Store", name: "Selected Store" };
  }, [storeId, list]);

  const loadOptions = useCallback(
    (inputValue) => {
      const search = String(inputValue || "").trim();

      // If empty, use the first page we already loaded.
      if (!search) {
        return Promise.resolve([DEFAULT_OPTION, ...(Array.isArray(list) ? list : [])]);
      }

      // Only hit the API for 3+ character searches.
      if (search.length < 3) {
        return Promise.resolve([DEFAULT_OPTION]);
      }

      // Debounce API calls: only fetch once the user stops typing.
      return new Promise((resolve) => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          fetchStores(search)
            .then((stores) => {
              // Ignore stale responses.
              if (requestId !== requestIdRef.current) {
                resolve([DEFAULT_OPTION]);
                return;
              }

              resolve([
                DEFAULT_OPTION,
                ...(Array.isArray(stores) ? stores : []),
              ]);
            })
            .catch(() => resolve([DEFAULT_OPTION]));
        }, 400);
      });
    },
    [list, fetchStores]
  );

  return (
    <AsyncSelect
      isClearable
      cacheOptions
      styles={{
        menu: (provided) => ({ ...provided, zIndex: 9999 }),
      }}
      isLoading={loading}
      defaultOptions={[DEFAULT_OPTION, ...list]}
      isDisabled={isDisabled}
      value={selectedValue}
      getOptionLabel={getStoreLabel}
      getOptionValue={(opt) => (opt?.isDefault ? "" : String(opt?.id))}
      loadOptions={loadOptions}
      onChange={(opt) => {
      if (!opt || opt.isDefault) {
        // Reset to default list and remove any searched options from the dropdown.
        setSelectKey((k) => k + 1);
        onStoreIdChange?.(null);
        fetchStores("");
        return;
      }
      onStoreIdChange?.(opt.id ?? opt.store_id ?? null);
      }}
      placeholder="Search or select store..."
      key={selectKey}
    />
  );
};

export default CustomStoreSelect;

